import { useEffect, useId, useRef } from "react";
import { IconPtaszek } from "../icons/index.js";

/**
 * F-8: radio i checkbox. Kwadracik/kolko 18px, zaznaczone w --primary, klikalna CALA
 * etykieta o wysokosci min. 38px (A-3).
 *
 * Natywny input siedzi pod spodem (przezroczysty, zerowej wielkosci), a widoczny
 * znacznik rysuje `.choice__box`. Dzieki temu zostaja: klawiatura, strzalki w grupie
 * radio, stan indeterminate i powiazanie z etykieta - a wyglada jak w gotowcu.
 *
 * F-7 celowo NIE ma tu odpowiednika: switch jest dopuszczony wylacznie dla ustawien
 * dzialajacych NATYCHMIAST, a ConvoLens nie ma zadnego takiego pola.
 */

export interface OpcjaWyboru<T extends string = string> {
  wartosc: T;
  etykieta: string;
  /** P-4: doprecyzowanie pod etykieta (np. dostepnosc modelu). */
  opis?: string;
  wylaczona?: boolean;
}

interface RadioGroupProps<T extends string> {
  /** Widoczna etykieta grupy. A-5: grupa musi miec nazwe dla czytnika. */
  etykieta: string;
  /** Wspolna nazwa pola formularza. */
  nazwa: string;
  wartosc: T;
  opcje: readonly OpcjaWyboru<T>[];
  onZmiana: (wartosc: T) => void;
  "aria-describedby"?: string;
}

export function RadioGroup<T extends string>({
  etykieta,
  nazwa,
  wartosc,
  opcje,
  onZmiana,
  "aria-describedby": ariaDescribedby,
}: RadioGroupProps<T>): React.JSX.Element {
  const idEtykiety = useId();

  return (
    <div className="choice-group">
      <span className="field-label" id={idEtykiety}>
        {etykieta}
      </span>
      <ul className="choice-list" role="radiogroup" aria-labelledby={idEtykiety} aria-describedby={ariaDescribedby}>
        {opcje.map((opcja) => (
          <li key={opcja.wartosc}>
            <label className={`choice${opcja.wylaczona ? " choice--wylaczona" : ""}`}>
              <input
                type="radio"
                name={nazwa}
                value={opcja.wartosc}
                checked={opcja.wartosc === wartosc}
                disabled={opcja.wylaczona}
                onChange={() => onZmiana(opcja.wartosc)}
              />
              <span className="choice__box choice__box--radio" aria-hidden="true">
                <span className="choice__dot" />
              </span>
              <span className="choice__tresc">
                {opcja.etykieta}
                {opcja.opis && <span className="choice__opis">{opcja.opis}</span>}
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface CheckboxProps {
  etykieta: string;
  zaznaczony: boolean;
  onZmiana: (zaznaczony: boolean) => void;
  /** TB-5: stan posredni dla "zaznacz wszystkie" przy czesciowym zaznaczeniu. */
  nieokreslony?: boolean;
  wylaczony?: boolean;
  opis?: string;
  nazwa?: string;
}

export function Checkbox({
  etykieta,
  zaznaczony,
  onZmiana,
  nieokreslony = false,
  wylaczony = false,
  opis,
  nazwa,
}: CheckboxProps): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);

  // `indeterminate` nie istnieje jako atrybut HTML - da sie go ustawic tylko z JS.
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = nieokreslony && !zaznaczony;
  }, [nieokreslony, zaznaczony]);

  return (
    <label className={`choice${wylaczony ? " choice--wylaczona" : ""}`}>
      <input
        ref={inputRef}
        type="checkbox"
        name={nazwa}
        checked={zaznaczony}
        disabled={wylaczony}
        onChange={(e) => onZmiana(e.target.checked)}
      />
      <span className="choice__box choice__box--check" aria-hidden="true">
        <IconPtaszek size={12} className="choice__mark" />
        <span className="choice__dash" />
      </span>
      <span className="choice__tresc">
        {etykieta}
        {opis && <span className="choice__opis">{opis}</span>}
      </span>
    </label>
  );
}
